import clsx from 'clsx';
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomButton from 'components/CustomButton';
import { useStyles } from './styles';

const Props = {
  classes: PropTypes.shape({
    paper: PropTypes.string,
    icon: PropTypes.string,
    closeButton: PropTypes.string,
    content: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  size: PropTypes.string,
  icon: PropTypes.string,
  iconPlacement: PropTypes.oneOf(['top', 'middle']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actionText: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
  cancelText: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
  actionButton: PropTypes.bool,
  cancelButton: PropTypes.bool,
  disableActionButton: PropTypes.bool,
  disableCancelButton: PropTypes.bool,
  onActionClick: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  variant: PropTypes.string,
};

const DefaultProps = {
  classes: {},
  size: 'xs',
  iconPlacement: 'top',
  cancelText: 'Cancel',
  actionButton: true,
  cancelButton: true,
};

export default function BaseModal({
  classes: propClasses,
  open,
  size,
  fullWidth,
  icon,
  iconPlacement,
  fill,
  title,
  subtitle,
  content,
  actionText,
  cancelText,
  actionButton,
  cancelButton,
  disableActionButton,
  disableCancelButton,
  onActionClick,
  onClose,
  isLoading,
  variant,
  header,
}) {
  const classes = useStyles();

  return (
    <Dialog
      classes={{
        paper: clsx(classes.paper, propClasses.paper, variant === 'height' && classes.height),
      }}
      maxWidth={size}
      fullWidth={fullWidth || true}
      open={open}
      onClose={onClose}
    >
      <div className={clsx(classes.closeButton, propClasses.closeButton)}>
        <IconButton onClick={onClose} size="large" aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </div>

      {icon && iconPlacement === 'top' && (
        <img className={clsx(classes.icon, propClasses.icon)} src={icon} alt="Icon" />
      )}

      {header && <DialogTitle>{header}</DialogTitle>}

      {title && (
        <DialogTitle className={classes.title}>
          <Typography variant="h5" component="p" gutterBottom={!!subtitle}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="h6" component="p">
              {subtitle}
            </Typography>
          )}{' '}
        </DialogTitle>
      )}

      {icon && iconPlacement === 'middle' && (
        <img className={clsx(classes.icon, propClasses.icon)} src={icon} alt="Icon" />
      )}

      {content && (
        <DialogContent
          className={clsx(classes.content, propClasses.content, fill && classes.contentFill)}
        >
          {content}
        </DialogContent>
      )}

      {!fill && (
        <div className={classes.actions}>
          {cancelButton && (
            <CustomButton variant="outlined" disabled={disableCancelButton} onClick={onClose}>
              {cancelText}
            </CustomButton>
          )}

          {actionButton && (
            <CustomButton disabled={disableActionButton} onClick={onActionClick}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : actionText}
            </CustomButton>
          )}
        </div>
      )}
    </Dialog>
  );
}

BaseModal.propTypes = Props;
BaseModal.defaultProps = DefaultProps;
